import { FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { message } from "antd";
import { useForm } from "antd/es/form/Form";

import QuotationService, { DEFAULT_PAYMENT_PLAN_CODE } from "../../../../../../../../../shared/services/quotation";
import {
  Activity,
  CoveragePerPlan,
  GenerateAPQuotationServiceRequest,
  GenerateAPQuotationServiceResponse,
  PrimaPremio,
  QuotationCoverage
} from "../../../../../../../../../shared/interfaces";
import { GetCommertialsConditionsResponse } from "../../../../../../../../../shared/interfaces/services/get-commertials-conditions-service";

import { StepThreeFormContext } from "./StepThreeFormContext";
import { GenericDataFormContext, QuotationInfoContext } from "../../../../../shared/contexts";
import { StepTwoFormContext } from "../../../StepTwo/contexts/StepTwoForm";
import { WizardDataFormContext } from "../../../../Wizard/contexts/WizardDataForm";

import { 
  QuotationStep, 
  QuotationButton, 
  QUOTATION_BUTTON,
  LOADING_SAVED_QUOTATIONS_STATES,
} from "../../../../../shared/interfaces";


import { 
  generateInputID, 
  generateUniqueActivityKey, 
  getActivitiesCodeFromError, 
  getActivitiesErrorMessage, 
  getCoverageAndActivityKeyByID,
} from "../../components/Quotation/utils";
import { getValueDefaultPaymentMethod } from "../../../../../utils";
import { scrollToEmitButton } from "../../../../../../../../../shared/utils";

const {
  STEP_THREE_DATA_FILLING,
  STEP_THREE_DATA_FILLED,
  STEP_TWO_DATA_FILLED,
} = LOADING_SAVED_QUOTATIONS_STATES;
export interface Variation {
  max:    number;
  min:    number;
};

export interface ActivitiesForm {
  [key: string]: string | number | boolean;
};
export interface PaymentMethodForm {
  [key: string]:          string | number | undefined;
  paymentMethod:          string;
  commission:             number;
  extraVariable:          number;
  variation?:             number;
};

export const StepThreeFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const [ activitiesForm ] = useForm<ActivitiesForm>();
  const [ paymentMethodsForm ] = useForm<PaymentMethodForm>();

  const [ quotationResponse, setQuotationResponse ] = useState<GenerateAPQuotationServiceResponse>();
  const [ reQuotationResponse, setReQuotationResponse ] = useState<GenerateAPQuotationServiceResponse>();

  const [ maxAndMinVariation, setMaxAndMinVariation ] = useState<Variation>();
  const [ commertialConditions, setCommertialConditions ] = useState<GetCommertialsConditionsResponse>();

  const [ labelButton, setLabelButton ] = useState<QuotationButton>(quotationResponse ? QUOTATION_BUTTON.RECOTIZAR : QUOTATION_BUTTON.COTIZAR);

  // Rastrear si el efecto automático ya se ejecutó para evitar duplicadas
  const hasAutoExecutedRef = useRef(false);

  const [ activitiesWithError, setActivitiesWithError ] = useState<Array<string>>([]);

  const [ paymentMethodFormValues, setPaymentMethodFormValues ] = useState<PaymentMethodForm>();

  const [coveragesLoaded, setCoveragesLoaded] = useState(false);

  const { 
    quotationInfo, 
    currentStep, 
    savedQuotation, 
    setCurrentStep,
    loadSaveQuotationState,
    setLoadSaveQuotationState,
    loadingSavedQuotation,
  } = useContext(QuotationInfoContext);
  const {
    setCanShowSpinner,
    allCoveragesPerPlanRisk,
    paymentMethodsList,
    setPaymentMethodsList,
    setPaymentMethods,
    getCoveragesPerPlanRisk,
  } = useContext(GenericDataFormContext);

  const { quotationForm, setQuotationForm } = useContext(WizardDataFormContext);

  const { 
    planWaschanged, 
    setPlanWaschanged, 
    propertyDataForm,
    activities,
  } = useContext( StepTwoFormContext );

  const [initialActivitiesForm, setInitialActivitiesForm] = useState<ActivitiesForm>();
  const [initialPaymentMethodForm, setInitialPaymentMethodForm] = useState<PaymentMethodForm>();
  const [activeTab, setActiveTab] = useState<string>(activities[0]?.activity);

  const planSelected = propertyDataForm?.getFieldValue('plans');
  
  const coveragesPerPlan = useMemo(
    () => allCoveragesPerPlanRisk.find(el => (el.plan as string) === planSelected)?.coverages ?? [], 
    [allCoveragesPerPlanRisk, planSelected]
  );

  const getMaxAndMinVariation = useCallback(async ( branch: string ) => {
      try {
        const response = await QuotationService.getIncreaseAndDecreaseOfEPV( branch );
        if( response.data ) {
          setMaxAndMinVariation({
            max: response.data.puntosDeAumentoPermitidos,
            min: -response.data.puntosDeDisminucionPermitidos,
          });
        };
      } catch (error) {} finally {
        setCanShowSpinner(false)
      };
  }, [ setMaxAndMinVariation, setCanShowSpinner ])

  const getCommertialConditions = useCallback(async ( branch: string ) => {
    try {
      const response = await QuotationService.getComertialsConditions( branch );
      if( response.data ) setCommertialConditions( response.data );
    } catch (error) {}
  }, [ setCommertialConditions ])

  const clearPrimaFieldsOnFormChange = useCallback(() => {
      const allFields = activitiesForm.getFieldsValue()
      const allPrimaKeys = Object.keys( allFields ).filter( el => el.includes('prima') );
      activitiesForm.resetFields( allPrimaKeys );
  }, [ activitiesForm ]);

  const refreshPaymentPlansByVigency = useCallback(
    async (article?: string, vigencyFrom?: string, vigencyTo?: string) => {
      if (!article || !vigencyFrom || !vigencyTo) return;

      try {
        const response = await QuotationService.getPaymentPlans(
          article,
          vigencyFrom,
          vigencyTo
        );

        const forms = response.data?.formasDePago ?? [];

        const options = forms.map(
          ({ descripcion, nroPlanDePago, codigoFormaDePago }) => ({
            label: descripcion,
            value: `${codigoFormaDePago}-${nroPlanDePago}`,
          })
        );

        setPaymentMethodsList(forms);
        setPaymentMethods(options);

        const currentPaymentMethod = paymentMethodsForm.getFieldValue(
          'paymentMethod'
        );
        const currentExists = forms.some(
          (el) =>
            `${el.codigoFormaDePago}-${el.nroPlanDePago}` ===
            currentPaymentMethod
        );

        if (!currentExists) {
          const defaultPaymentMethod = forms.find(
            (el) => el.codigoFormaDePago === DEFAULT_PAYMENT_PLAN_CODE
          );

          if (defaultPaymentMethod) {
            paymentMethodsForm.setFieldValue(
              'paymentMethod',
              `${defaultPaymentMethod.codigoFormaDePago}-${defaultPaymentMethod.nroPlanDePago}`
            );
          }
        }
      } catch (error) {}
    },
    [setPaymentMethodsList, setPaymentMethods, paymentMethodsForm]
  );

  const setFormFields = useCallback(
      (activities: Array<Activity>, primaPremioOperacion?: PrimaPremio) => {
        //* Set payment method, comission and epv.
        const paymentMethodCode =
          paymentMethodsForm.getFieldValue('paymentMethod');
        const defaultPaymentMethod = paymentMethodsList?.find(
          (el) => el.codigoFormaDePago === DEFAULT_PAYMENT_PLAN_CODE
        );
  
        if (
          paymentMethodCode &&
          paymentMethodsList?.find((el) => `${el.codigoFormaDePago}-${el.nroPlanDePago}` === paymentMethodCode)
        ) {
          paymentMethodsForm.setFieldValue('paymentMethod', paymentMethodCode);
        } else {
          paymentMethodsForm.setFieldValue('paymentMethod', `${defaultPaymentMethod?.codigoFormaDePago}-${defaultPaymentMethod?.nroPlanDePago}`);
        }
  
        if (primaPremioOperacion) {
          paymentMethodsForm.setFieldValue(
            'commission',
            Number(primaPremioOperacion.porcentajeComision)
          );
          paymentMethodsForm.setFieldValue(
            'extraVariable',
            Number(primaPremioOperacion.porcentajeExtraPrimaVariable)
          );
        }
  
        //* Set activities and coverages fields
        activities?.forEach(({ coberturas, codigo, parentesco, rangoDeEdad }) => {
          const activityKey = generateUniqueActivityKey(
            codigo,
            parentesco,
            rangoDeEdad,
            quotationForm?.plan!
          );
  
          coberturas?.forEach(({ id, sumaAsegurada, prima }) => {
            const sumaAseguradaInputName = generateInputID(
              id as string,
              activityKey
            );
  
            const primaInputName = generateInputID(
              id as string,
              activityKey,
              'prima'
            );
            activitiesForm.setFieldValue(
              sumaAseguradaInputName,
              Number(sumaAsegurada)
            );
            activitiesForm.setFieldValue(primaInputName, Number(prima));
          });
        });
      },
      [
        activitiesForm,
        paymentMethodsForm,
        paymentMethodsList,
        quotationForm,
      ]
  );

  const handlerOnClickQuotation = useCallback(
      async (form: GenerateAPQuotationServiceRequest) => {
        try {
          setCanShowSpinner(true);
  
          const response = await QuotationService.generateAPQuotation(form);
  
          if (response.data) {
  
            //recover ID from original coverate and set it into response
            response.data.actividades.forEach(activity => {
              activity?.coberturas?.forEach( coverage => {
                const originalCoverage = coveragesPerPlan.find( item => item.codigo  === coverage.cobertura && item.codigoRiesgo === coverage.riesgo );
                coverage.id = originalCoverage?.id;
              });
            });
  
            setQuotationResponse(response.data);
  
            const updatedActivityList = response.data.actividades.map((el) => {
              delete el['primaPremio']; //* These info is not used, and we don´t need it in the next request.
              el['cantidadDeCoberturas'] = el.coberturas?.length.toString();
              return el;
            });
  
            setQuotationForm(
              (prevState: GenerateAPQuotationServiceRequest | undefined) => ({
                ...prevState!,
                actividades: updatedActivityList,
                vigenciaDesde: form.vigenciaDesde,
                vigenciaHasta: form.vigenciaHasta,
              })
            );
  
            setFormFields(
              response.data.actividades,
              response.data.primaPremioOperacion
            );
            scrollToEmitButton();
            setLabelButton(QUOTATION_BUTTON.EMITIR);
          }
        } catch (error) {
        } finally {
          setCanShowSpinner(false);
        }
      },
      [
        coveragesPerPlan,
        setFormFields,
        setQuotationForm,
        setCanShowSpinner,
        setQuotationResponse,
        setLabelButton,
      ]
  );
  
  const handlerOnClickReQuotation = useCallback(
      async (form: GenerateAPQuotationServiceRequest) => {
        try {
          setCanShowSpinner(true);
          const response = await QuotationService.regenerateAPQuotation(form);
  
          if (response.data) {
  
            //recover ID from original coverate and set it into response
            response.data.actividades.forEach(activity => {
              activity?.coberturas?.forEach( coverage => {
                const originalCoverage = coveragesPerPlan.find( item => item.codigo  === coverage.cobertura && item.codigoRiesgo === coverage.riesgo );
                coverage.id = originalCoverage?.id;
              });
            });
  
            setReQuotationResponse(response.data);
  
            const updatedActivityList = response.data.actividades.map((el) => {
              delete el['primaPremio']; //* These info is not used, and we don´t need it in the next request.
              el['cantidadDeCoberturas'] = el.coberturas?.length.toString();
              return el;
            });
  
            setQuotationForm(
              (prevState: GenerateAPQuotationServiceRequest | undefined) => ({
                ...prevState!,
                actividades: updatedActivityList,
                vigenciaDesde: form.vigenciaDesde,
                vigenciaHasta: form.vigenciaHasta,
              })
            );

            await refreshPaymentPlansByVigency(
              quotationInfo?.article,
              form.vigenciaDesde,
              form.vigenciaHasta
            );



            setFormFields(updatedActivityList);

            scrollToEmitButton();
            setLabelButton(QUOTATION_BUTTON.EMITIR);
  
            setPaymentMethodFormValues(paymentMethodsForm.getFieldsValue());
            
            setReQuotationResponse(response.data);
            setQuotationResponse(response.data);
          }
        } catch (error) {
        } finally {
          setCanShowSpinner(false);
          
        }
      },
      [
        coveragesPerPlan,
        setQuotationForm,
        setCanShowSpinner,
        setReQuotationResponse,
        setFormFields,
        setLabelButton,
        setPaymentMethodFormValues,
        refreshPaymentPlansByVigency,
        quotationInfo,
      ]
  );

  const addCoverageToActivity = useCallback(
      (coverage: CoveragePerPlan, sumaAsegurada: string, activityKey: string) => {
  
          const updatedActivityList = quotationForm?.actividades?.map(
              (activity) => {
                  const key = generateUniqueActivityKey(
                      activity.codigo,
                      activity.parentesco,
                      activity.rangoDeEdad,
                      quotationForm?.plan!
                  );
          
                  if (key === activityKey) {
                      const newCoverage: QuotationCoverage = {
                        riesgo: coverage.codigoRiesgo,
                        cobertura: coverage.codigo,
                        sumaAsegurada,
                        prima: '0',
                        pormilaje: '0',
                      };
          
                      activity.coberturas?.push(newCoverage);
                      
                      activity.cantidadDeCoberturas = String(
                      activity.coberturas?.length
                      );
                  }
          
                  return activity;
              }
          );
      
          setQuotationForm(
              (prevState: GenerateAPQuotationServiceRequest | undefined) => ({
                ...prevState!,
                actividades: updatedActivityList,
              })
          );
      },
      [quotationForm, setQuotationForm]
  );
  
  const filterDisabledCoverage = useCallback(
      (coverage: CoveragePerPlan, activityKey: string) => {
          const updatedActivityList = quotationForm?.actividades?.map(
              (activity) => {
              const key = generateUniqueActivityKey(
                  activity.codigo,
                  activity.parentesco,
                  activity.rangoDeEdad,
                  quotationForm?.plan!
              );
      
              if (key === activityKey) {
                  activity.coberturas = activity.coberturas?.filter(
                  (el) => el.id !== coverage.id
                  );
                  activity.cantidadDeCoberturas =
                  activity.coberturas?.length.toString();
              }
      
              return activity;
              }
          );
  
          setQuotationForm(
              (prevState: GenerateAPQuotationServiceRequest | undefined) => ({
              ...prevState!,
              actividades: updatedActivityList,
              })
          );
      },
      [quotationForm, setQuotationForm]
  );

  const updateFormFieldsBeforeQuotation = useCallback(async (): Promise<
    GenerateAPQuotationServiceRequest | any
  > => {
      try {        
        await activitiesForm.validateFields();

        quotationForm?.actividades?.forEach(act => act.coberturas = []);

        setActivitiesWithError([]);

        let form: GenerateAPQuotationServiceRequest = quotationForm!;

        //* Update information of each coverage into quotation response form.
        const allFields = activitiesForm.getFieldsValue();

        const allSumaAseguradasKeys: Array<string> = Object.keys(
            allFields
        ).filter((el: string) => el.includes('suma'));
        const allCheckboxKeys: Array<string> = Object.keys(allFields).filter(
            (el: string) => el.includes('checkbox')
        );

        allSumaAseguradasKeys.forEach((key) => {
            //* If we have a value on suma asegurada field, and checkbox is checked, we have to add coverage to each activity in quotation form
            if (allFields[key]?.toString()?.length) {
            const { activityKey, id } = getCoverageAndActivityKeyByID(key);

            const checkboxName = generateInputID(
                id,
                activityKey,
                'checkbox'
            );
            const coverage = coveragesPerPlan.find(
                (el) => el.id === id
            );
            if (coverage && activitiesForm.getFieldValue(checkboxName)) {
                return addCoverageToActivity(coverage, allFields[key] as string, activityKey);
            }
            }
        });

        allCheckboxKeys.forEach((key) => {
            //* If checkbox is not checked, we have to filter coverages from each activity in quotation form
            if (!allFields[key]) {
            const { activityKey, id } = getCoverageAndActivityKeyByID(key);

            const coverage = coveragesPerPlan.find(
                (el) => el.id === id
            );

            if (coverage) return filterDisabledCoverage(coverage, activityKey);
            }
        });

        //* Set information of Payment Methods Form
        const paymentMethodCode = paymentMethodsForm.getFieldInstance('paymentMethod') ? paymentMethodsForm?.getFieldValue('paymentMethod').split('-')[1] : getValueDefaultPaymentMethod(paymentMethodsList)

        //* quotation response only exists after quotation. These props are add for requotation.
        if (quotationResponse) {
            const epvValue = paymentMethodsForm.getFieldValue('extraVariable');

            form = {
            ...form,
            porcentajeExtraPrimaVariable: epvValue?.toString(),
            planDePago: paymentMethodCode,
            };
        }
        else{
          form = {
            ...form,
            planDePago: paymentMethodCode,
          };
        }

        return form;
    } catch (error: any) {
      if (error?.errorFields?.length) {
        const activitiesWithError = getActivitiesCodeFromError(
        error?.errorFields
        );
        setActivitiesWithError(activitiesWithError);
        const errorMessage = getActivitiesErrorMessage(activitiesWithError);
        message.error(errorMessage);
      }
    }
  }, [
    coveragesPerPlan,
    addCoverageToActivity,
    paymentMethodsForm,
    quotationResponse,
    activitiesForm,
    setQuotationForm,
    quotationForm,
    setActivitiesWithError,
    paymentMethodsList,
  ]);

  const handlerOnClickEmit = useCallback(() => {
      setPaymentMethodFormValues(paymentMethodsForm.getFieldsValue());
  
      setCurrentStep(QuotationStep.FOUR);
  }, [setCurrentStep, setPaymentMethodFormValues, paymentMethodsForm]);

  const fillStepThreeFromSavedQuotation = useCallback(() => {
    setLoadSaveQuotationState(STEP_THREE_DATA_FILLING);

    const { 
      actividades, 
      planSeleccionado,
      formaDePago,
    } = savedQuotation!;

    const initialActivitiesFormValues: ActivitiesForm = {};
    
    actividades.forEach(({ coberturasSeleccionadas, codigo, parentesco, rangoDeEdad, categoria }: any) => {
      const uniqueKey =  generateUniqueActivityKey(
        codigo,
        parentesco,
        rangoDeEdad,
        String(planSeleccionado)
      );

      coberturasSeleccionadas.forEach(({ codigo, sumaAsegurada, prima, codigoRiesgo }: any) => {
        const checkboxKey = generateInputID(`${codigo}-${codigoRiesgo}`, uniqueKey, 'checkbox');
        const sumaAseguradaKey = generateInputID(`${codigo}-${codigoRiesgo}`, uniqueKey);
        const primaKey = generateInputID(`${codigo}-${codigoRiesgo}`, uniqueKey, 'prima');

        initialActivitiesFormValues[checkboxKey] = true;
        initialActivitiesFormValues[sumaAseguradaKey] = sumaAsegurada;
        initialActivitiesFormValues[primaKey] = prima;
      });
    });

    setInitialActivitiesForm(initialActivitiesFormValues)

    setInitialPaymentMethodForm({
      paymentMethod: `${formaDePago.codigo}-${formaDePago.codigoDePlanDePagos}`,
      extraVariable: Number(formaDePago.porcentajeExtraPrimaVariable),
      commission: Number(formaDePago.porcentajeComision),
      variation: Number(formaDePago.porcentajeComision) - Number(commertialConditions?.porcentajeDeComision),
    });

    //* Se setea vacio ya que no tenemos la primera respuesta de la COTIZACIÓN en un retome. 
    setQuotationResponse({
      totalSumaAsegurada: '',
      actividades: [],
      primaPremioOperacion: {
        porcentajeExtraPrimaVariable: formaDePago.porcentajeExtraPrimaVariable,
        extraPrimaVariable: '',
        porcentajeComision: formaDePago.porcentajeComision,
        importeComision: '',
        porcentajeRecargoFinanciero: '',
        importeRecargoFinanciero: '',
        importeExtraPrimaFija: '',
        importeSelladoDelRiesgo: '',
        importeSelladoDeEmpresa: '',
        porcentajeImpuestosInternos: '',
        importeImpuestosInternos: '',
        porcentajeServiciosSociales: '',
        importeServiciosSociales: '',
        tasaSsn: '',
        importeTasaSsn: '',
        porcentajeIva: '',
        importeIva: '',
        porcentajeIvaNoInscripto: '',
        importeIvaNoInscripto: '',
        porcentajeIvaPrecepcion: '',
        importeIvaPercepcion: '',
        importeReservado: '',
        importeIngresosBrutosRiesgo: '',
        importeIngresosBrutosEmpresa: '',
      },
      primaOperacion: '',
      premioOperacion: '',
    });

    setLoadSaveQuotationState(STEP_THREE_DATA_FILLED);

    setLabelButton(QUOTATION_BUTTON.RECOTIZAR);
  }, [
    savedQuotation,
    commertialConditions,
    setInitialActivitiesForm,
    setPaymentMethodFormValues,
    setQuotationResponse,
    setLoadSaveQuotationState,
    setLabelButton,
  ]);

  const handlerOnClick = useCallback(async () => {
    if (labelButton === QUOTATION_BUTTON.EMITIR) return handlerOnClickEmit();

    try {
      const form: GenerateAPQuotationServiceRequest = await updateFormFieldsBeforeQuotation();

      if (form) {
        if (labelButton === QUOTATION_BUTTON.COTIZAR) {
          return await handlerOnClickQuotation(form);
        };

        if (labelButton === QUOTATION_BUTTON.RECOTIZAR) {
          return await handlerOnClickReQuotation(form);
        };
      };
    } catch (error) {};
  }, [
    handlerOnClickQuotation,
    handlerOnClickReQuotation,
    handlerOnClickEmit,
    updateFormFieldsBeforeQuotation,
    labelButton,
  ]);

  useEffect(() => {
    if(loadSaveQuotationState === STEP_TWO_DATA_FILLED && commertialConditions) {
      // Resetear el flag cuando se inicia un nuevo ciclo de carga
      hasAutoExecutedRef.current = false;
      fillStepThreeFromSavedQuotation();
    };
  }, [loadSaveQuotationState, commertialConditions]);

  useEffect(() => {
    const loadCoverages = async () => {
      setCoveragesLoaded(false);
  
      if (quotationForm?.actividades?.length) {     
        const activitySelected = quotationForm.actividades[0];
        const riskCode = String(activitySelected.categoria).padStart(3, '0');      
        if (planSelected?.length) {
          await getCoveragesPerPlanRisk(riskCode, activitySelected.codigo);
        }
      }
  
      setCoveragesLoaded(true); 
    };
  
    loadCoverages();
  }, [quotationForm, planSelected]);
  

  // Ref para siempre tener la versión actualizada de handlerOnClick (evita stale closure en el useEffect de auto-trigger)
  const handlerOnClickRef = useRef(handlerOnClick);
  useEffect(() => {
    handlerOnClickRef.current = handlerOnClick;
  }, [handlerOnClick]);

  useEffect(() => {
    if (
      loadSaveQuotationState === STEP_THREE_DATA_FILLED &&
      !loadingSavedQuotation &&
      coveragesLoaded &&
      !hasAutoExecutedRef.current
    ) {
      hasAutoExecutedRef.current = true;
      handlerOnClickRef.current();
    }
  }, [loadSaveQuotationState, loadingSavedQuotation, coveragesLoaded]);
  

  useEffect(() => {
      if( planWaschanged && currentStep === QuotationStep.TWO ) {
          activitiesForm.resetFields();
      }

      if( planWaschanged && currentStep === QuotationStep.THREE ) {
          setPlanWaschanged(false);
      }
  }, [ planWaschanged, activitiesForm, currentStep ]);
  
  useEffect(() => {
      if( paymentMethodFormValues ) {
          for(let key in paymentMethodFormValues) {
              paymentMethodsForm.setFieldValue( key, paymentMethodFormValues[key] )
          }   
      }
      /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
      if(quotationInfo?.branch && currentStep === QuotationStep.THREE) {
          if(!maxAndMinVariation) getMaxAndMinVariation( quotationInfo?.branch );
      }
      /* eslint-disable-next-line */
  }, [ quotationInfo, currentStep, maxAndMinVariation ]);

  useEffect(() => {
      if(quotationInfo?.branch && !commertialConditions) {
          getCommertialConditions( quotationInfo?.branch );
      }
      /* eslint-disable-next-line */
  }, [ quotationInfo, commertialConditions ]);

  const values = useMemo(() => ({
    coveragesPerPlan,
    maxAndMinVariation,
    quotationResponse,
    setQuotationResponse,
    reQuotationResponse,
    setReQuotationResponse,
    labelButton,
    setLabelButton,
    activitiesWithError,
    setActivitiesWithError,
    activitiesForm,
    paymentMethodsForm,
    clearPrimaFieldsOnFormChange,
    setPaymentMethodFormValues,
    initialActivitiesForm, 
    setInitialActivitiesForm,
    initialPaymentMethodForm,
    handlerOnClick,
    activeTab, 
    setActiveTab,
  }), [
    coveragesPerPlan,
    maxAndMinVariation,
    quotationResponse,
    setQuotationResponse,
    reQuotationResponse,
    setReQuotationResponse,
    labelButton,
    setLabelButton,
    activitiesWithError,
    setActivitiesWithError,
    activitiesForm,
    paymentMethodsForm,
    clearPrimaFieldsOnFormChange,
    setPaymentMethodFormValues,
    initialActivitiesForm, 
    setInitialActivitiesForm,
    initialPaymentMethodForm,
    handlerOnClick,
    activeTab, 
    setActiveTab
  ]);

  return (
    <StepThreeFormContext.Provider value={ values }>              
      { children }
    </StepThreeFormContext.Provider>
  )
}